import React, { useState, useEffect } from 'react';

import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';
import { pickingListPresetService } from '@services/operator/pickingListPresetService';
import { message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import FormContent from './Form';

interface MerchantSummary {
  id: string;
  name?: string;
}

interface PickingListPresetRecord {
  id: string;
  merchant_id?: string | null;
  merchant?: MerchantSummary | null;
  warehouse?: { id?: string | null } | null;
  [key: string]: unknown;
}

interface OperatorWarehouse {
  id?: string;
  title?: string;
}

const OperatorPickingListPresetEdit: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const { merchantShippingTypes = [], merchantSalesChannels = [] } = useOperatorData();
  const { operator } = useOperatorAuth();
  const operatorWarehouseId = (operator?.warehouse as OperatorWarehouse | undefined)?.id ?? null;
  // PickingListPreset.belongs_to :merchant 是 optional（organization-scope preset
  // 合法 merchant_id = null）→ 跳過 hook 的 missing-merchant console.error。
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync({ optional: true });
  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState<PickingListPresetRecord | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await pickingListPresetService.get(id);
        if (response.success) {
          // 通知 wrapper：拿到 record 後讓 useRecordMerchantSync 把 OperatorDataContext.currentMerchant
          // 對齊到該 record 的 merchant，這樣 merchantShippingTypes / merchantSalesChannels 才會跟著
          // 切到正確 merchant 的選項，避免在錯 merchant 的下拉清單上顯示 raw UUID 或寫壞資料。
          handleRecordLoaded(response.data);
          setInitialData(response.data);
        } else {
          message.error(response.message || t('common.error.load_failed'));
        }
      } catch (error) {
        console.error('Fetch preset error:', error);
        message.error(t('common.error.load_failed'));
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  // Gate render：在 record 載入完成 *且* OperatorDataContext.currentMerchant 對齊到該 record 的
  // merchant 之前都不要 render Form，避免 form 用前一個 merchant 的 reference data 渲染下拉。
  if (loading || !initialData || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <FormContent
        loading={loading}
        setLoading={setLoading}
        initialData={initialData}
        salesChannels={merchantSalesChannels}
        shippingTypes={merchantShippingTypes}
        merchantIdForCreate={null}
        warehouseIdForCreate={operatorWarehouseId}
      />
    </div>
  );
};

export default OperatorPickingListPresetEdit;
